import { type KeyboardEvent, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { MoreIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';

export interface RowMenuItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean | undefined;
  /** `danger` for the item that takes something away (desactivar, dar de baja). */
  tone?: 'default' | 'danger';
}

/**
 * The «⋯» at the end of a row: the two or three actions of a person or a mailbox behind
 * one button, so the table reads as a table and not as a row of buttons.
 *
 * Own implementation, no dependency: a button with `aria-haspopup="menu"` and
 * `aria-expanded`, a list with `role="menu"` and `menuitem`s, arrows to move, Escape or
 * a click outside to close, and the focus back on the button when it closes. The panel is
 * `position: fixed` from the button's rectangle, because the table lives in a scroll
 * region (`overflow-x: auto`) that would clip anything absolute at the last row; it
 * closes on scroll and resize instead of trying to follow.
 */
export function RowMenu({ label, items }: { label: string; items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 4, right: Math.max(0, window.innerWidth - rect.right) });
    // The first item takes the focus, as a menu is expected to.
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)');
    first?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function close() {
      setOpen(false);
    }
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', close);
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', close);
      document.removeEventListener('scroll', close, true);
    };
  }, [open]);

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const enabled = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [],
    );
    if (enabled.length === 0) return;
    const index = enabled.indexOf(document.activeElement as HTMLElement);
    let next: number | null = null;
    if (event.key === 'ArrowDown') next = index < 0 ? 0 : (index + 1) % enabled.length;
    else if (event.key === 'ArrowUp')
      next = index < 0 ? enabled.length - 1 : (index - 1 + enabled.length) % enabled.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = enabled.length - 1;
    else if (event.key === 'Tab') {
      // Tab leaves the menu: it closes and the focus carries on from the button.
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }
    if (next === null) return;
    event.preventDefault();
    enabled[next]?.focus();
  }

  function select(item: RowMenuItem) {
    setOpen(false);
    buttonRef.current?.focus();
    item.onSelect();
  }

  return (
    <div ref={rootRef} className="inline-flex">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'text-ink-2 hover:bg-paper-2 hover:text-ink inline-flex size-8 items-center justify-center rounded-md transition-colors',
          'focus-visible:outline-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          open && 'bg-paper-2 text-ink',
        )}
      >
        <MoreIcon size={18} />
      </button>
      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          style={position ? { top: position.top, right: position.right } : undefined}
          className="border-line bg-card fixed z-30 flex min-w-44 flex-col rounded-md border py-1 shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => select(item)}
              className={cn(
                'px-3 py-1.5 text-left text-[13.5px] whitespace-nowrap transition-colors',
                'focus-visible:bg-paper-2 hover:bg-paper-2 focus-visible:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50',
                item.tone === 'danger' ? 'text-danger' : 'text-ink',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
